import React from 'react';
import {AlertTriangle, Calendar, MapPin, FileText} from 'lucide-react';
import {StatusBadge} from '@/components/UI/StatusBadge';
import {formatCurrency, formatDateTime, formatIsConfirmed, formatIsPayed, formatIsSentToExec} from '@/utils/formatters';
import type {Penalty} from '@/types';

interface PenaltiesSectionProps {
    penalties: Penalty[];
}

export const PenaltiesSection: React.FC<PenaltiesSectionProps> = ({penalties}) => {
    if (penalties.length === 0) {
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <div className="text-center">
                    <AlertTriangle className="mx-auto h-12 w-12 text-gray-400"/>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No penalties</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        This user has no penalty records.
                    </p>
                </div>
            </div>
        );
    }

    const totalAmount = penalties.reduce((sum, penalty) => sum + parseFloat(penalty.amount), 0);
    const unpaidPenalties = penalties.filter(p => !p.payed);

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <FileText className="h-6 w-6 text-gray-400"/>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">
                                        Total Penalties
                                    </dt>
                                    <dd className="text-lg font-medium text-gray-900">
                                        {penalties.length}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <AlertTriangle className="h-6 w-6 text-red-400"/>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">
                                        Unpaid
                                    </dt>
                                    <dd className="text-lg font-medium text-gray-900">
                                        {unpaidPenalties.length}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <FileText className="h-6 w-6 text-green-400"/>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">
                                        Total Amount
                                    </dt>
                                    <dd className="text-lg font-medium text-gray-900">
                                        {formatCurrency(totalAmount)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Penalties List */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Penalty Records
                    </h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Number
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Confirmed
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Send to Exec
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Location
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {penalties.map((penalty) => (
                            <tr key={penalty.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    #{penalty.id}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                    <div>
                                        <p className="font-medium">{penalty.number}</p>
                                        {penalty.violation_type && (
                                            <p className="text-gray-500 text-xs mt-1">
                                                Type: {penalty.violation_type}
                                            </p>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="font-semibold">
                      {formatCurrency(parseFloat(penalty.amount))}
                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <StatusBadge status={formatIsPayed(penalty.payed)}/>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <StatusBadge status={formatIsConfirmed(penalty.confirmed)} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <StatusBadge status={formatIsSentToExec(penalty.sent_to_exec_service)} />
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                    {penalty.location ? (
                                        <div className="flex items-center">
                                            <MapPin className="h-4 w-4 text-gray-400 mr-1"/>
                                            {penalty.location}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">Not specified</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <div className="flex items-center">
                                        <Calendar className="h-4 w-4 text-gray-400 mr-1"/>
                                        {formatDateTime(penalty.documents_date)}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PenaltiesSection;
